// Copyright (c) StrangeBee 2023

import React, { ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGetSettings } from '@Queries/UISettingsQueries';
import { useLocalMessage } from '@Helpers/locale';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { classList } from '@Helpers/className';
import { CaseCreateImport } from './CaseCreateImport';
import { CaseCreateForm } from './CaseCreateForm';
import useListQuery from '@Hooks/useListQuery';
import client from '@Client';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import useValidateForm from '@Hooks/useValidateForm';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { CaseCreateMISPImport } from './CaseCreateMISPImport';
import { ICONS } from '@Constants/iconsApp';
import { StatusContext } from '@Context/StatusProvider';
import { CaseInput, CaseTemplateOutput } from 'thehive-sdk';
import { trimTags } from '@Helpers/tags';

import './CaseCreateManage.scss';

type CaseCreateManageViews = 'manage' | 'form' | 'import' | 'misp';

interface CaseCreateManageProps {
    triggerElem: ReactElement;
    title?: string;
    onCaseCreate: (caseData: CaseInput) => Promise<any>;
    onImport: (formData: FormData) => Promise<any>;
    onMispImport: (formData: FormData) => Promise<any>;
    disabledImport?: boolean;
    initialValues?: Partial<CaseInput>;
    defaultCaseTemplate?: string;
}

export const CaseCreateManage = ({
    triggerElem,
    onCaseCreate,
    onImport,
    onMispImport,
    disabledImport = true,
    initialValues,
    defaultCaseTemplate = '',
}: CaseCreateManageProps): ReactElement => {
    const i = useLocalMessage();

    const { data: settingsData } = useGetSettings();
    const { hasMispConnector } = useContext(StatusContext);

    const canNotCreateEmptyCase = settingsData?.hideEmptyCaseButton;

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!initialValues,
    });
    const caseImportValidationForm = useValidateForm();
    const caseMISPImportValidationForm = useValidateForm();

    const [width, setWidth] = useState('50vw');
    const [caseTemplate, setCaseTemplate] = useState<CaseTemplateOutput>();
    const [view, setView] = useState<CaseCreateManageViews>('manage');

    const { data: caseTemplateList = [], refetch: caseTemplateRefetch } = useListQuery<CaseTemplateOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'listCaseTemplate' }],
                name: 'get-case-templates',
                extraData: ['pageTemplateHeaders'],
            },
            paginationOptions: {
                itemsPerPage: 999,
                page: 0,
            },
        },
        manual: true,
        fetchOnStart: true,
        controller: client.CaseTemplateController,
    });

    const setDefaultCaseTemplate = useCallback(() => {
        setCaseTemplate(caseTemplateList?.find(({ name }) => name === defaultCaseTemplate));
    }, [defaultCaseTemplate, caseTemplateList]);

    useEffect(() => {
        if (defaultCaseTemplate) {
            setDefaultCaseTemplate();
        }
    }, [defaultCaseTemplate, caseTemplateList, setDefaultCaseTemplate]);

    const onReset = useCallback(() => {
        setDefaultCaseTemplate();
        setView('manage');
        setWidth('50vw');
        setFieldsTouched(false);
        caseImportValidationForm.setFieldsTouched(false);
        caseMISPImportValidationForm.setFieldsTouched(false);
    }, [setFieldsTouched, caseImportValidationForm, caseMISPImportValidationForm, setDefaultCaseTemplate]);

    const caseTemplateListSorted = useMemo(
        () =>
            caseTemplateList
                ?.map(({ displayName, name }) => ({
                    label: displayName || name,
                    value: name,
                }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [caseTemplateList],
    );

    const Manage = useMemo(
        () => (
            <div className="case-create-manage">
                <div className="case-create-manage__section">
                    <ICONS.FormOutlined className="case-create-manage__section-icon" />

                    <span className="case-create-manage__section-info__label">{i('case.create.choose.type')}</span>
                </div>

                <div className="case-create-manage__section" {...{ 'data-testid': 'case-creation-empty' }}>
                    <div
                        className={classList('case-create-manage__card', {
                            'card-disabled': canNotCreateEmptyCase,
                        })}
                        onClick={(): void => {
                            if (canNotCreateEmptyCase) {
                                return;
                            }
                            setView('form');
                            setWidth('50vw');
                            setCaseTemplate(undefined);
                        }}
                    >
                        <span className="case-create-manage__section-title">
                            <ICONS.EmptyCaseIcon className="case-create-manage__section-icon" />

                            <span className="case-create-manage__section-label">{i('case.create.choose.scratch')}</span>
                        </span>
                    </div>
                </div>

                <div className="case-create-manage__section" {...{ 'data-testid': 'case-creation-template' }}>
                    <div
                        className={classList('case-create-manage__card')}
                        onClick={() => {
                            if (caseTemplate) {
                                setView('form');
                                setWidth('50vw');
                            }
                        }}
                    >
                        <span className="case-create-manage__section-title">
                            <ICONS.CaseTemplateIcon className="case-create-manage__section-icon" />
                            <span className="case-create-manage__section-label">
                                {i('case.create.choose.template')}
                            </span>
                        </span>
                        <THSelect
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                            options={caseTemplateListSorted}
                            onDropdownVisibleChange={(open): void => {
                                open && caseTemplateRefetch();
                            }}
                            onSelect={() => {
                                setView('form');
                                setWidth('50vw');
                            }}
                            filterOption={(searched, value) => {
                                if (value && typeof value.label === 'string') {
                                    return value.label.toLowerCase().indexOf(searched.toLowerCase()) >= 0;
                                }
                                return false;
                            }}
                            value={caseTemplate?.name}
                            onChange={(value) => setCaseTemplate(caseTemplateList.find(({ name }) => name === value))}
                            {...{ 'data-testid': 'case-template-select' }}
                            showSearch
                        />
                    </div>
                </div>

                <div className="case-create-manage__section" {...{ 'data-testid': 'case-creation-archive' }}>
                    <div
                        className={classList('case-create-manage__card', {
                            'card-disabled': disabledImport,
                        })}
                        {...(disabledImport
                            ? {}
                            : {
                                  onClick: (): void => {
                                      setWidth('50vw');
                                      setView('import');
                                  },
                              })}
                    >
                        <span className="case-create-manage__section-title">
                            <ICONS.BoxArchiveIcon className="case-create-manage__section-icon" />
                            <span className="case-create-manage__section-label">{i('case.create.choose.import')}</span>
                        </span>
                    </div>
                </div>

                {hasMispConnector && (
                    <div className="case-create-manage__section" {...{ 'data-testid': 'case-creation-misp' }}>
                        <div
                            className={classList('case-create-manage__card', { 'card-disabled': disabledImport })}
                            onClick={() => {
                                if (disabledImport) {
                                    return;
                                }

                                setWidth('50vw');
                                setView('misp');
                            }}
                        >
                            <span className="case-create-manage__section-title">
                                <ICONS.ImportOutlined className="case-create-manage__section-icon" />
                                <span className="case-create-manage__section-label">
                                    {i('case.create.choose.misp')}
                                </span>
                            </span>
                        </div>
                    </div>
                )}
            </div>
        ),
        [
            canNotCreateEmptyCase,
            caseTemplateList,
            i,
            caseTemplateRefetch,
            caseTemplate,
            disabledImport,
            hasMispConnector,
            caseTemplateListSorted,
        ],
    );

    const views = useMemo(
        () => ({
            manage: { render: () => Manage, title: i('case.create.drawer.title.choose') },
            form: {
                render: (toggleDrawer: () => void) => (
                    <CaseCreateForm
                        form={form}
                        onFinish={({ startDate, sharingParameters, tags = [], ...values }) =>
                            onCaseCreate({
                                ...values,
                                sharingParameters: sharingParameters
                                    ?.filter(({ isShared }) => isShared)
                                    // eslint-disable-next-line
                                    .map(({ editable, isShared, ...sharingValues }) => ({
                                        ...sharingValues,
                                        share: isShared,
                                    })), // eslint-disable-line
                                startDate: typeof startDate !== 'number' ? (startDate as any).valueOf() : startDate,
                                tags: trimTags(tags),
                            }).then(() => {
                                toggleDrawer();
                                setFieldsTouched(false);
                            })
                        }
                        caseTemplate={caseTemplate}
                        isValid={isValid && (!initialValues ? fieldsTouched : true)}
                        onChange={() => {
                            onFormValuesChange();
                            setFieldsTouched(true);
                        }}
                        onCancel={() => {
                            toggleDrawer();
                            setFieldsTouched(false);
                        }}
                        initialValues={initialValues}
                    />
                ),
                title: caseTemplate
                    ? i('case.create.drawer.btn.create-from', {
                          name: caseTemplate?.displayName || caseTemplate?.name || '',
                      })
                    : i('case.create.drawer.btn.create'),
            },
            import: {
                render: (toggleDrawer: () => void) => (
                    <CaseCreateImport
                        caseImportValidationForm={caseImportValidationForm}
                        onImport={onImport}
                        toggleDrawer={toggleDrawer}
                    />
                ),
                title: i('case.create.drawer.btn.import'),
            },
            misp: {
                render: (toggleDrawer: () => void) => (
                    <CaseCreateMISPImport
                        caseMISPImportValidationForm={caseMISPImportValidationForm}
                        onImport={onMispImport}
                        toggleDrawer={toggleDrawer}
                    />
                ),
                title: i('case.create.drawer.btn.import-from'),
            },
        }),
        [
            Manage,
            caseTemplate,
            form,
            onCaseCreate,
            onImport,
            onMispImport,
            isValid,
            onFormValuesChange,
            i,
            initialValues,
            setFieldsTouched,
            caseImportValidationForm,
            caseMISPImportValidationForm,
            fieldsTouched,
        ],
    );

    return (
        <THDrawer
            title={
                <div className="case-create-manage__title">
                    {view !== 'manage' && (
                        <THButton type="ghost" onClick={(): void => onReset()}>
                            {'<'}
                        </THButton>
                    )}
                    {views[view].title}
                </div>
            }
            triggerElem={triggerElem}
            className={classList('case-create-manage__drawer', 'drawer-form')}
            width={width}
            disableClose={
                fieldsTouched || caseImportValidationForm.fieldsTouched || caseMISPImportValidationForm.fieldsTouched
            }
            afterVisibleChange={(visible) => {
                if (!visible) {
                    onReset();
                }
            }}
        >
            {(toggleDrawer) => views[view].render(toggleDrawer)}
        </THDrawer>
    );
};
